// Hangul (Korean) jamo decomposition + jamo-level diff.
//
// A Hangul syllable block (가–힣) is composed of 2–3 jamo: a lead consonant
// (choseong), a vowel (jungseong), and an optional tail consonant (jongseong).
// Unicode lays them out arithmetically, so decomposition needs no table lookup
// beyond the jamo lists — it's pure math, deterministic, no ML.
//
// Purpose: give learners feedback at the level they actually make mistakes —
// "you wrote ㅗ, it should be ㅓ" — instead of a silent pass/fail, and to surface
// which confusable pairs (ㅓ/ㅗ, ㅐ/ㅔ, ㅡ/ㅜ …) a user keeps swapping so drills
// can target them. Shared, framework-free; mirrored in lib/utils/jamo.dart.

const S_BASE = 0xac00;
const L_COUNT = 19;
const V_COUNT = 21;
const T_COUNT = 28;
const N_COUNT = V_COUNT * T_COUNT; // 588
const S_COUNT = L_COUNT * N_COUNT; // 11172

const LEADS = 'ㄱㄲㄴㄷㄸㄹㅁㅂㅃㅅㅆㅇㅈㅉㅊㅋㅌㅍㅎ'.split('');
const VOWELS = 'ㅏㅐㅑㅒㅓㅔㅕㅖㅗㅘㅙㅚㅛㅜㅝㅞㅟㅠㅡㅢㅣ'.split('');
// index 0 = no tail
const TAILS = ['', 'ㄱ', 'ㄲ', 'ㄳ', 'ㄴ', 'ㄵ', 'ㄶ', 'ㄷ', 'ㄹ', 'ㄺ', 'ㄻ', 'ㄼ',
  'ㄽ', 'ㄾ', 'ㄿ', 'ㅀ', 'ㅁ', 'ㅂ', 'ㅄ', 'ㅅ', 'ㅆ', 'ㅇ', 'ㅈ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ'];

export type JamoPart = 'lead' | 'vowel' | 'tail';

/** Decompose a single character. Returns [lead, vowel, tail] (tail '' if none)
 * for a composed Hangul syllable, or null for anything else (jamo already
 * decomposed, spaces, Latin, punctuation, other scripts). */
export function decomposeSyllable(ch: string): [string, string, string] | null {
  const code = ch.codePointAt(0);
  if (code === undefined) return null;
  const s = code - S_BASE;
  if (s < 0 || s >= S_COUNT) return null;
  const lead = Math.floor(s / N_COUNT);
  const vowel = Math.floor((s % N_COUNT) / T_COUNT);
  const tail = s % T_COUNT;
  // Indices are provably in range (arithmetic on a validated syllable), so the
  // non-null assertions are safe under noUncheckedIndexedAccess.
  return [LEADS[lead]!, VOWELS[vowel]!, TAILS[tail]!];
}

/** Flatten a string to its jamo sequence; non-Hangul chars pass through as-is.
 * "학교" -> ["ㅎ","ㅏ","ㄱ","ㄱ","ㅛ"]. Useful for confusable-pair stats. */
export function decompose(text: string): string[] {
  const out: string[] = [];
  for (const ch of text) {
    const syl = decomposeSyllable(ch);
    if (syl) {
      out.push(syl[0], syl[1]);
      if (syl[2]) out.push(syl[2]);
    } else {
      out.push(ch);
    }
  }
  return out;
}

export interface JamoMismatch {
  /** syllable index in the CORRECT string */
  index: number;
  part: JamoPart;
  expected: string; // jamo (or char) that should be there
  got: string; // what the user wrote ('' = missing)
}

export interface JamoDiff {
  match: boolean;
  mismatches: JamoMismatch[];
  /** Vowel/consonant substitutions as [expected, got] — the raw material for
   * confusable-pair drills (e.g. ["ㅓ","ㅗ"]). Excludes missing/extra. */
  confusables: Array<[string, string]>;
}

/** Compare an attempt against the correct spelling at the jamo level, aligned by
 * syllable position. Designed for the common beginner case (right length, a jamo
 * or two swapped); extra/missing syllables are reported as whole-syllable
 * mismatches rather than a full edit-distance alignment. */
export function jamoDiff(correct: string, attempt: string): JamoDiff {
  const cc = [...correct];
  const ac = [...attempt];
  const mismatches: JamoMismatch[] = [];
  const confusables: Array<[string, string]> = [];
  const n = Math.max(cc.length, ac.length);

  for (let i = 0; i < n; i++) {
    const c = cc[i];
    const a = ac[i];
    if (c === a) continue; // fast path incl. identical non-Hangul chars

    const cs = c !== undefined ? decomposeSyllable(c) : null;
    const as = a !== undefined ? decomposeSyllable(a) : null;

    if (cs && as) {
      const parts: JamoPart[] = ['lead', 'vowel', 'tail'];
      for (let p = 0; p < 3; p++) {
        const want = cs[p]!; // p ∈ 0..2 over a 3-tuple — safe
        const has = as[p]!;
        if (want !== has) {
          mismatches.push({ index: i, part: parts[p]!, expected: want, got: has });
          // both present => a substitution (confusable); '' tail counts as add/drop
          if (want && has) confusables.push([want, has]);
        }
      }
    } else {
      // one side missing, or a non-Hangul char differs — report whole char
      mismatches.push({
        index: i,
        part: 'lead',
        expected: c ?? '',
        got: a ?? '',
      });
    }
  }

  return { match: mismatches.length === 0, mismatches, confusables };
}
